import React, { useEffect, useRef } from 'react';
import Tooltip from 'bootstrap/js/dist/tooltip';

/**
 * Safely initializes Bootstrap tooltips
 * 
 * @param selector - CSS selector for tooltip elements, defaults to '[data-bs-toggle="tooltip"]'
 * @returns Array of created tooltip instances
 */
export function initTooltips(selector: string = '[data-bs-toggle="tooltip"]'): Tooltip[] {
  try {
    // Find all tooltip trigger elements
    const tooltipTriggerList = document.querySelectorAll(selector);
    
    // Create tooltip instances and filter out any failures
    return Array.from(tooltipTriggerList)
      .map(el => {
        try {
          return new Tooltip(el as HTMLElement);
        } catch (err) {
          console.error("Error creating tooltip:", err);
          return null;
        }
      })
      .filter(Boolean) as Tooltip[];
  } catch (err) {
    console.error("Error initializing tooltips:", err);
    return [];
  }
}

/**
 * Safely disposes Bootstrap tooltips
 * 
 * @param tooltipInstances - Array of tooltip instances to dispose
 */
export function disposeTooltips(tooltipInstances: Tooltip[]): void {
  try {
    // Safely dispose each tooltip instance
    if (tooltipInstances && tooltipInstances.length > 0) {
      tooltipInstances.forEach(tooltip => {
        try {
          if (tooltip) tooltip.dispose();
        } catch (err) {
          // Silently fail individual tooltip disposal
        }
      });
    }
    
    // Also clean up any tooltip elements from the DOM
    const tooltipElements = document.querySelectorAll('.tooltip');
    tooltipElements.forEach(element => {
      try {
        element.remove();
      } catch (err) {
        // Silently fail individual element removal
      }
    });
  } catch (err) {
    console.error("Error safely disposing tooltips:", err);
  }
}

/**
 * React hook for safely managing Bootstrap tooltips
 * 
 * @param dependencies - Array of dependencies that should trigger tooltip re-initialization
 * @param selector - CSS selector for tooltip elements, defaults to '[data-bs-toggle="tooltip"]'
 * @param delay - Delay in ms before initializing tooltips, useful for waiting for DOM to render
 * @returns Object containing tooltipInstances for any manual handling
 * 
 * Example usage:
 * ```
 * import { useTooltips } from '../utils/tooltipUtils';
 * 
 * function MyComponent() {
 *   useTooltips([isLoading, data]);
 *   // Rest of component...
 * }
 * ```
 */
export function useTooltips(
  dependencies: any[] = [], 
  selector: string = '[data-bs-toggle="tooltip"]',
  delay: number = 100
): { tooltipInstances: Tooltip[] } {
  const tooltipInstances = useRef<Tooltip[]>([]);
  
  useEffect(() => {
    // Safely dispose any existing tooltips
    disposeTooltips(tooltipInstances.current);
    tooltipInstances.current = [];
    
    // Add a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      tooltipInstances.current = initTooltips(selector);
    }, delay);
    
    // Clean up on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutId);
      disposeTooltips(tooltipInstances.current);
      tooltipInstances.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
  
  return { tooltipInstances: tooltipInstances.current };
} 